import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { createEffectFactory } from '@core/ngrx-legacy';
import type { OfferFull } from '@core/offers-types';
import { OfferStatus, StatusChangeType } from '@core/offers-types';

import { JobsFullActions } from './jobs-full.store';
import { OfferScope } from '../../../types';
import { JobsHttpService } from '../jobs-http.service';

@Injectable({
  providedIn: 'root',
})
export class JobsFullEffects {
  private readonly actions$ = inject(Actions);
  private readonly jobsHttpService = inject(JobsHttpService);

  public effects = createEffectFactory(JobsFullActions, this.actions$);

  public fetchItem$ = this.effects(JobsFullActions.fetchOne, ({ payload }) =>
    this.jobsHttpService
      .fetchItem$(payload)
      .pipe(map(resp => ({ id: payload.id, job: resp.offer, context: payload.context }))),
  );

  public create$ = this.effects(JobsFullActions.create, ({ payload }) =>
    this.jobsHttpService.create$({ offer: payload.offer }).pipe(
      switchMap(response => [JobsFullActions.createSuccess({ payload: { data: response.offer } })]),
      catchError(error => [JobsFullActions.createFailed({ payload: error })]),
    ),
  );

  public createFromRequisition$ = this.effects(
    JobsFullActions.createFromRequisition,
    ({ payload }) =>
      this.jobsHttpService.createFromRequisition$(payload.requisitionId).pipe(
        switchMap(response => [
          JobsFullActions.createFromRequisitionSuccess({ payload: { data: response.offer } }),
        ]),
        catchError(error => [JobsFullActions.createFromRequisitionFailed({ payload: error })]),
      ),
  );

  public createFromTemplate$ = this.effects(JobsFullActions.createFromTemplate, ({ payload }) =>
    this.jobsHttpService.createFromTemplate$(payload.templateId).pipe(
      switchMap(response => [
        JobsFullActions.createFromTemplateSuccess({ payload: { data: response.offer } }),
      ]),
      catchError(error => [JobsFullActions.createFromTemplateFailed({ payload: error })]),
    ),
  );

  public createFromPosition$ = this.effects(JobsFullActions.createFromPosition, ({ payload }) =>
    this.jobsHttpService.createFromPosition$(payload.positionId).pipe(
      switchMap(response => [
        JobsFullActions.createFromPositionSuccess({ payload: { data: response.offer } }),
      ]),
      catchError(error => [JobsFullActions.createFromPositionFailed({ payload: error })]),
    ),
  );

  public update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.update),
      switchMap(({ payload }) =>
        this.jobsHttpService.update$({ offer: payload.offer }).pipe(
          switchMap(response => [
            JobsFullActions.updateSuccess({
              payload: { id: response.offer.id, data: response.offer },
            }),
          ]),
          catchError(error =>
            of(JobsFullActions.updateFailed({ payload: { id: payload.offer.id, error } })),
          ),
        ),
      ),
    ),
  );

  public follow$ = this.effects(JobsFullActions.follow, ({ payload }) =>
    this.jobsHttpService.follow(payload.id).pipe(
      switchMap(response => [JobsFullActions.followSuccess({ payload: { job: response.offer } })]),
      catchError(error => of(JobsFullActions.followFailed({ payload: { error, id: payload.id } }))),
    ),
  );

  public unfollow$ = this.effects(JobsFullActions.unfollow, ({ payload }) =>
    this.jobsHttpService.unfollow(payload.id).pipe(
      switchMap(response => [
        JobsFullActions.unfollowSuccess({ payload: { job: response.offer } }),
      ]),
      catchError(error =>
        of(JobsFullActions.unfollowFailed({ payload: { error, id: payload.id } })),
      ),
    ),
  );

  public duplicate$ = this.effects(JobsFullActions.duplicate, ({ payload }) =>
    this.jobsHttpService.duplicate$(payload.id).pipe(
      switchMap(response => [
        JobsFullActions.duplicateSuccess({ payload: { offer: response.offer } }),
      ]),
      catchError(error => [
        JobsFullActions.duplicateFailed({ payload: { id: payload.id, error } }),
      ]),
    ),
  );

  public changeStatus$ = this.effects(JobsFullActions.changeStatus, ({ payload }) => {
    let status$: Observable<{ offer: OfferFull }>;
    const targetScope =
      payload.status === StatusChangeType.Archive ? OfferScope.Archived : OfferScope.Active;
    const isChangingScope =
      payload.currentStatus === OfferStatus.Archived || payload.status === StatusChangeType.Archive;

    switch (payload.status) {
      case StatusChangeType.Close:
        status$ = this.jobsHttpService.close(payload.id);
        break;
      case StatusChangeType.Archive:
        status$ = this.jobsHttpService.archive(payload.id);
        break;
      case StatusChangeType.Unarchive:
        status$ = this.jobsHttpService.unarchive(payload.id);
        break;
      case StatusChangeType.Draft:
        status$ = this.jobsHttpService.draft(payload.id);
        break;
      case StatusChangeType.Publish:
        status$ = this.jobsHttpService.publish(payload.id);
        break;
      case StatusChangeType.Unpublish:
        status$ = this.jobsHttpService.unpublish(payload.id);
        break;
      default:
        throw new Error(`Unknown status: ${payload.status}`);
    }

    return status$.pipe(
      switchMap(response => [
        JobsFullActions.changeStatusSuccess({
          payload: { offer: response.offer, targetScope, isChangingScope },
        }),
      ]),
      catchError(error => [
        JobsFullActions.changeStatusFailed({ payload: { error, id: payload.id } }),
      ]),
    );
  });

  public preview$ = this.effects(JobsFullActions.preview, ({ payload: { offer } }) =>
    this.jobsHttpService.preview$({ data: offer }).pipe(
      switchMap(({ previewUrl }) => [JobsFullActions.previewSuccess({ payload: { previewUrl } })]),
      catchError(error => [JobsFullActions.previewFailed({ payload: { error } })]),
    ),
  );

  public uploadSocialMediaImage$ = this.effects(
    JobsFullActions.uploadSocialMediaImage,
    ({ payload }) =>
      this.jobsHttpService.uploadSocialMediaImage$(payload.id, payload.file).pipe(
        switchMap(response => [
          JobsFullActions.uploadSocialMediaImageSuccess({ payload: response }),
        ]),
        catchError(error => [
          JobsFullActions.uploadSocialMediaImageFailed({ payload: { id: payload.id, error } }),
        ]),
      ),
  );

  public uploadCoverImage$ = this.effects(JobsFullActions.uploadCoverImage, ({ payload }) =>
    this.jobsHttpService.uploadCoverImage$(payload.id, payload.file).pipe(
      switchMap(response => [JobsFullActions.uploadCoverImageSuccess({ payload: response })]),
      catchError(error => [
        JobsFullActions.uploadCoverImageFailed({ payload: { id: payload.id, error } }),
      ]),
    ),
  );

  public updateSlug$ = this.effects(JobsFullActions.updateSlug, ({ payload }) =>
    this.jobsHttpService.updateSlug$(payload.id, payload.slug).pipe(
      switchMap(response => [
        JobsFullActions.updateSlugSuccess({ payload: { id: payload.id, offer: response.offer } }),
      ]),
      catchError(error => [
        JobsFullActions.updateSlugFailed({ payload: { id: payload.id, error } }),
      ]),
    ),
  );

  public insertDataFromTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.insertDataFromTemplate),
      switchMap(({ payload }) =>
        this.jobsHttpService
          .insertDataFromTemplate$(
            payload.offerId,
            payload.jobTemplateId,
            payload.insertMethod,
            payload.placementsMovements,
          )
          .pipe(map(response => response.offer)),
      ),
      switchMap(offer => [
        JobsFullActions.insertDataFromTemplateSuccess({ payload: { data: { offer } } }),
        JobsFullActions.updateSuccess({ payload: { id: offer.id, data: offer } }),
      ]),
      catchError(error => of(JobsFullActions.insertDataFromTemplateFailed({ payload: error }))),
    ),
  );
}
